import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, Cake, Trash2, Save, Loader2, Search } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];

export default function BirthdaysPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/birthdays/${active.id}`).then((r) => setList(r.data));
    api.get(`/features/birthdays-config/${active.id}`).then((r) => setCfg(r.data));
    api.get(`/bot/members?guild_id=${active.id}&limit=500`).then((r) => setMembers(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const add = async (e) => {
    e.preventDefault();
    if (!picked || !day || !month) {
      toast.error("Pick a member + month + day");
      return;
    }
    const date = `${month}-${String(day).padStart(2, "0")}`;
    await api.post(`/features/birthdays/${active.id}`, {
      user_id: picked.id, username: picked.username, date,
    });
    toast.success(`Birthday saved for ${picked.username}`);
    setPicked(null);
    setDay(""); setMonth("");
    refresh();
  };

  const remove = async (uid) => {
    await api.delete(`/features/birthdays/${active.id}/${uid}`);
    refresh();
  };

  const saveCfg = async () => {
    if (!cfg) return;
    setSavingCfg(true);
    try {
      await api.post(`/features/birthdays-config/${active.id}`, cfg);
      toast.success("Birthday settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingCfg(false); }
  };

  const filteredMembers = members
    .filter((m) => m.username.toLowerCase().includes(memberSearch.toLowerCase()))
    .slice(0, 8);

  return (
    <div data-testid="birthdays-panel" className="space-y-8 max-w-5xl">
      <div>
        <div className="overline mb-2"><Cake className="inline w-4 h-4 mr-2 -mt-1" />GAMES & FUN</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Birthdays</h1>
        <p className="text-zinc-400 mt-2">Save members' birthdays and let the bot wish them automatically every day.</p>
      </div>

      {/* CONFIG */}
      {cfg && (
        <div className="tactical-card p-6" data-testid="bd-config">
          <div className="overline mb-1">// AUTO-ANNOUNCE</div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Birthday Message Settings</h2>
          <button type="button" onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
            className={`w-full p-3 border flex items-center justify-between rounded-lg mb-4 ${cfg.enabled ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`}
            data-testid="bd-cfg-toggle">
            <span className="font-heading uppercase tracking-wider text-sm">Auto-announce on birthday</span>
            <span className={`w-10 h-5 relative rounded-full ${cfg.enabled ? "bg-brand-orange" : "bg-brand-line"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full ${cfg.enabled ? "left-5" : "left-0.5"}`} />
            </span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Announce channel</label>
              <select className="tactical-input" value={cfg.announce_channel_id || ""} onChange={(e) => setCfg({ ...cfg, announce_channel_id: e.target.value || null })} data-testid="bd-cfg-channel">
                <option value="">— Select —</option>
                {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
              </select>
              <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Bot will post here when someone has a birthday</div>
            </div>
            <div>
              <label className="overline block mb-2">Daily check schedule</label>
              <div className="tactical-input bg-brand-line/30 cursor-not-allowed">Every hour at minute :00 (UTC)</div>
              <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Auto-runs in background — no setup needed</div>
            </div>
          </div>

          <div className="mt-4">
            <label className="overline block mb-2">Birthday message template</label>
            <textarea className="tactical-input min-h-[80px]" value={cfg.message_template} onChange={(e) => setCfg({ ...cfg, message_template: e.target.value })} data-testid="bd-cfg-template" />
            <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Variables: {"{user}"} = mention · {"{name}"} = username</div>
          </div>

          <div className="flex justify-end mt-5">
            <button onClick={saveCfg} disabled={savingCfg} className="btn-primary" data-testid="bd-cfg-save">
              {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* ADD BIRTHDAY */}
      <form onSubmit={add} className="tactical-card p-6" data-testid="bd-form">
        <div className="overline mb-1">// ADD A BIRTHDAY</div>
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Pick a member</h2>

        {!picked ? (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input className="tactical-input pl-10" placeholder="Search server members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} data-testid="bd-search" />
            </div>
            {memberSearch && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto tactical-scroll">
                {filteredMembers.length === 0 && <div className="text-zinc-500 text-sm font-mono-tabular">No matches</div>}
                {filteredMembers.map((m) => (
                  <button key={m.id} type="button" onClick={() => { setPicked(m); setMemberSearch(""); }}
                    className="border border-brand-line hover:border-brand-orange p-3 rounded-lg flex items-center gap-3 text-left transition-colors"
                    data-testid={`bd-member-${m.id}`}>
                    {m.avatar ? <img src={m.avatar} alt="" className="w-9 h-9 rounded-md" /> : (
                      <div className="w-9 h-9 bg-brand-orange/10 border border-brand-orange rounded-md flex items-center justify-center font-bold text-brand-orange uppercase">{m.username[0]}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{m.username}</div>
                      {m.nickname && <div className="text-xs text-zinc-500 truncate">{m.nickname}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="border border-brand-orange bg-brand-orange/5 p-4 rounded-lg flex items-center gap-3">
              {picked.avatar ? <img src={picked.avatar} alt="" className="w-10 h-10 rounded-md" /> :
                <div className="w-10 h-10 bg-brand-orange/10 border border-brand-orange rounded-md flex items-center justify-center font-bold text-brand-orange uppercase">{picked.username[0]}</div>}
              <div className="flex-1">
                <div className="font-semibold">{picked.username}</div>
                <div className="font-mono-tabular text-xs text-zinc-500">ID: {picked.id}</div>
              </div>
              <button type="button" onClick={() => setPicked(null)} className="btn-ghost text-xs">Change</button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Month</label>
                <select className="tactical-input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="bd-month">
                  <option value="">—</option>
                  {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Day</label>
                <input type="number" min="1" max="31" className="tactical-input" placeholder="1-31" value={day} onChange={(e) => setDay(e.target.value)} data-testid="bd-day" />
              </div>
            </div>

            <div className="flex justify-end">
              <button type="submit" className="btn-primary" data-testid="bd-add-btn"><Plus className="w-4 h-4" /> Save birthday</button>
            </div>
          </div>
        )}
      </form>

      {/* LIST */}
      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl font-bold uppercase mb-4">Birthday Calendar ({list.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {list.length === 0 && <div className="text-zinc-500 font-mono-tabular text-sm py-6 col-span-full text-center">No birthdays added yet</div>}
          {list.map((b) => (
            <div key={b.user_id} className="border border-brand-line p-3 rounded-lg flex items-center gap-3" data-testid={`bd-row-${b.user_id}`}>
              <Cake className="w-5 h-5 text-brand-orange" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{b.username}</div>
                <div className="font-mono-tabular text-xs text-zinc-500">{b.date}</div>
              </div>
              <button onClick={() => remove(b.user_id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
